package one.healthium.log_food.ui.add_food

import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.profile.ui.food.models.FloatPart
import one.healthium.profile.ui.food.models.FoodUiModel
import one.healthium.profile.ui.food.models.FoodUiOptions
import one.healthium.profile.ui.food.models.Units
import one.healthium.profile.ui.food.models.toOptions
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import kotlin.time.Instant

internal class AddFoodViewModel(
    private val userInteractor: UserInteractor,
    appPreferences: AppPreferences,
) : BaseViewModel() {

    private val currentFood = MutableStateFlow<FoodUiModel?>(null)

    private val _state = MutableStateFlow(AddFoodUiState())
    val state = _state.asStateFlow()

    val uiMessage = UiMessageManager()

    init {
        combine(
            currentFood.filterNotNull(),
            appPreferences.isMetricFlow,
        ) { food, isMetric ->
            _state.update { state ->
                val availableUnits = AmountUiModel.availableUnits(
                    isMetric = isMetric,
                    tablespoon = food.tablespoon,
                    cups = food.cups,
                    pieces = food.pieces,
                    portion = food.portion,
                    isLiquid = food.isLiquid,
                )
                val portionUnit = availableUnits.find { it is Units.Portion }
                val defaultUnit = portionUnit ?: AmountUiModel.defaultUnit(isMetric = isMetric, isLiquid = food.isLiquid)
                val initialValue = when {
                    portionUnit != null -> 1
                    isMetric -> 10
                    else -> 1
                }
                state.copy(
                    currentFood = food,
                    foodAdded = false,
                    currentAmount = AmountUiModel(
                        integerPart = initialValue,
                        floatingPart = FloatPart.Zero,
                        unit = defaultUnit,
                        units = availableUnits,
                    )
                )
            }
        }.launch()
    }

    fun onAddClicked(options: FoodUiOptions, taskId: String?) {
        launch {
            _state.update { state -> state.copy(loading = true) }

            val foodOptions = options.toOptions()
            try {
                userInteractor.addFood(foodOptions, taskId)
                _state.update { state ->
                    state.copy(
                        foodAdded = true,
                        loading = false,
                    )
                }
            } catch (e: Exception) {
                Logger.e(e) { "Failed to add food" }
                _state.update { state -> state.copy(loading = false) }
                uiMessage.add(UiMessage.throwable(e))
            }
        }
    }

    fun onFoodChanged(food: FoodUiModel) {
        currentFood.value = food
    }

    fun onAmountChanged(amount: AmountUiModel) {
        _state.update { state -> state.copy(currentAmount = amount) }
    }

    fun onTimeChanged(time: Instant) {
        _state.update { state ->
            state.copy(
                selectedDate = time,
            )
        }
    }
}
